import {
    createLogicCondition,
    createLogicGroup,
    type CreateLogicNodeFactoryOptions,
    type LogicConditionNode,
    type LogicNode,
    type TradeLogicContextFeatureRef,
    type TradeLogicDraft
} from './model';

/**
 * URL-seeded veto condition for the fork-and-return editor flow
 * (docs/plans/REGIME_DEPENDENCE_PROFILE_FRONTEND_PLAN.md, "The action path").
 *
 * The regime dependence profile's veto candidates are always exclusion-shaped, so the only seed shape is
 * "feature != state" per excluded state. A multi-state veto expands into one `!=` condition per state —
 * `regime.marketRegime.compositeState` only allows the `=` / `!=` operators, so a single `not_in`
 * condition is not authorable for it.
 */
export interface TradeLogicSeedVetoCondition {
    kind: 'contextFeature';
    featureKey: string;
    operator: '!=';
    excludedStates: string[];
}

export const TRADE_LOGIC_SEED_CONDITION_PARAM_KEY = 'seedCondition';

export const REGIME_MARKET_COMPOSITE_FEATURE_KEY = 'regime.marketRegime.compositeState';

/**
 * The only features a URL seed may target, by construction. The regime profile's veto dimensions map
 * onto exactly these; anything else in a `seedCondition` param parses as invalid rather than seeding an
 * arbitrary condition into the editor.
 */
export const SEEDABLE_VETO_FEATURE_KEYS: ReadonlySet<string> = new Set([REGIME_MARKET_COMPOSITE_FEATURE_KEY]);

export const serializeTradeLogicSeedCondition = (seed: TradeLogicSeedVetoCondition): string => JSON.stringify(seed);

export type TradeLogicSeedConditionParseResult =
    /** No seedCondition param present. */
    | { status: 'absent' }
    /** A param was present but is not a valid seed. The editor surfaces this instead of silently dropping it. */
    | { status: 'invalid' }
    | { status: 'ok'; seed: TradeLogicSeedVetoCondition };

const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === 'string');

export const parseTradeLogicSeedCondition = (raw: string | null): TradeLogicSeedConditionParseResult => {
    if (raw === null || raw.trim() === '') return { status: 'absent' };

    let parsed: unknown;

    try {
        parsed = JSON.parse(raw);
    } catch {
        return { status: 'invalid' };
    }

    if (typeof parsed !== 'object' || parsed === null) return { status: 'invalid' };

    const candidate = parsed as Record<string, unknown>;

    if (candidate.kind !== 'contextFeature') return { status: 'invalid' };
    if (typeof candidate.featureKey !== 'string' || !SEEDABLE_VETO_FEATURE_KEYS.has(candidate.featureKey)) return { status: 'invalid' };
    if (candidate.operator !== '!=') return { status: 'invalid' };
    if (!isStringArray(candidate.excludedStates)) return { status: 'invalid' };

    const excludedStates = Array.from(new Set(candidate.excludedStates.map((state) => state.trim()).filter((state) => state !== '')));

    if (excludedStates.length === 0) return { status: 'invalid' };

    return {
        seed: {
            excludedStates,
            featureKey: candidate.featureKey,
            kind: 'contextFeature',
            operator: '!='
        },
        status: 'ok'
    };
};

/**
 * Canonical `kind: 'contextFeature'` ref with every optional field explicit, matching the shape the
 * registry template path emits (`schemaControlUtils.getConditionTemplateFeatureRef`). `offsetBars`
 * stays null deliberately — the backend plan documents free-typed offsets as a live-support footgun.
 *
 * `missingDataPolicy` must not be null: with no policy the condition engine hard-fails the whole
 * evaluation when regime data is absent at a timestamp (e.g. bars evaluated before the first regime
 * snapshot of an experiment window). `treatAsUnknown` makes an absent value evaluate the `!=` veto as
 * passing — an exclusion gate only blocks entries *positively classified* into an excluded state, which
 * mirrors the profile's retention math and keeps the vetoed variant's population comparable to its base.
 */
export const buildSeedConditionFeatureRef = (featureKey: string): TradeLogicContextFeatureRef => ({
    bindingKey: null,
    duration: null,
    entityKey: null,
    entityType: null,
    featureKey,
    filters: null,
    kind: 'contextFeature',
    lookbackBars: null,
    missingDataPolicy: 'treatAsUnknown',
    offsetBars: null,
    selectors: null,
    trailingMode: null,
    withinBars: null
});

export interface ApplySeedConditionResult {
    draft: TradeLogicDraft;
    /** True when at least one condition was added (the draft object is new in that case). */
    applied: boolean;
    /** States a condition was added for. */
    seededStates: string[];
    /** States skipped because an enabled, identical veto condition already exists in the draft. */
    skippedExistingStates: string[];
}

/**
 * States already vetoed by the draft. A condition counts ONLY when its entire ancestor chain is
 * enabled `all` groups: a disabled ancestor compiles the whole subtree away, and a condition inside an
 * `any`/`firstMatch`/`threshold`/`weighted` group, a `not` group (which INVERTS it), or a sequence step
 * does not unconditionally veto its state. Counting such a match would silently drop the state from the
 * seed — the traversal therefore refuses to descend past anything but an enabled `all` group, erring
 * toward redundant seeding over a skipped veto.
 */
const collectExistingVetoStates = (rootNode: LogicNode | null, featureKey: string): Set<string> => {
    const states = new Set<string>();

    const visit = (node: LogicNode) => {
        if (!node.enabled) return;

        if (node.kind === 'condition') {
            if (node.operator !== '!=' || node.featureRef?.featureKey !== featureKey) return;
            if (typeof node.value === 'string') states.add(node.value);
            return;
        }

        if (node.kind !== 'group' || node.operator !== 'all') return;

        for (const child of node.children) visit(child);
    };

    if (rootNode !== null) visit(rootNode);

    return states;
};

const featureDisplayName = (featureKey: string): string => featureKey.split('.').at(-1) ?? featureKey;

/**
 * Pre-adds the seeded veto to a freshly copied entry draft: one `!=` condition per excluded state,
 * clustered in a labeled "Regime veto" group so the seed reads (and can be removed) as one unit.
 *
 * - A root `all` group gains the veto group as a new child.
 * - Any other root (an `any`/`not`/threshold group, a sequence, or a leaf) is wrapped in a new `all`
 *   group so the veto constrains the whole entry instead of joining a disjunction.
 * - A state is skipped only when an identical `!=` condition already vetoes it unconditionally (see
 *   `collectExistingVetoStates`), so re-entering the flow against an already-gated entry cannot stack
 *   duplicates — while a match in dead or non-vetoing context is still seeded.
 */
export const applySeedConditionToDraft = (
    draft: TradeLogicDraft,
    seed: TradeLogicSeedVetoCondition,
    options: CreateLogicNodeFactoryOptions = {}
): ApplySeedConditionResult => {
    const existingStates = collectExistingVetoStates(draft.rootNode, seed.featureKey);
    const seededStates = seed.excludedStates.filter((state) => !existingStates.has(state));
    const skippedExistingStates = seed.excludedStates.filter((state) => existingStates.has(state));

    if (seededStates.length === 0) {
        return { applied: false, draft, seededStates: [], skippedExistingStates };
    }

    const displayName = featureDisplayName(seed.featureKey);

    const conditions: LogicConditionNode[] = seededStates.map((state) =>
        createLogicCondition(
            {
                featureRef: buildSeedConditionFeatureRef(seed.featureKey),
                label: `${displayName} != ${state}`,
                operator: '!=',
                value: state
            },
            options
        )
    );

    const vetoGroup = createLogicGroup(
        {
            children: conditions,
            label: 'Regime veto',
            operator: 'all'
        },
        options
    );
    vetoGroup.description = `Vetoes ${displayName} states: ${seededStates.join(', ')}. Seeded from the regime dependence profile.`;

    const { rootNode } = draft;

    let nextRootNode: LogicNode;

    if (rootNode === null) {
        nextRootNode = vetoGroup;
    } else if (rootNode.kind === 'group' && rootNode.operator === 'all') {
        nextRootNode = { ...rootNode, children: [...rootNode.children, vetoGroup] };
    } else {
        nextRootNode = createLogicGroup({ children: [rootNode, vetoGroup], label: 'All of', operator: 'all' }, options);
    }

    return {
        applied: true,
        draft: { ...draft, rootNode: nextRootNode },
        seededStates,
        skippedExistingStates
    };
};
